import styles from './index.module.scss';
import { fabric } from 'fabric';
import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import useCanvasZoom from '../hooks/useCanvasZoom';
import useCanvasMovement from '../hooks/useCanvasMovement';
import { RESIZE_IMG_CANVASNAME } from '@utils/CONST'


const CanvasLeftCom = forwardRef((props, ref) => {
    const { imageUrl, onMove, onZoom, zoom, initMinZoom } = props
    const isMounted = useRef(null);
    const canvasRef = useRef(null);
    const canvasInstance = useRef(null);
    const [imgWidth, setImgWidth] = useState()
    const [imgHeight, setImgHeight] = useState()

    useImperativeHandle(ref, () => ({
        setPosition: (position) => {
            if (canvasInstance.current) {
                canvasInstance.current.viewportTransform[4] += position.left
                canvasInstance.current.viewportTransform[5] += position.top
                canvasInstance.current.requestRenderAll();
            }
        }
    }))

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    const initCanvas = () => {
        if (!canvasRef.current?.parentElement) return;
        const container = canvasRef.current.parentElement;
        const { width, height } = container.getBoundingClientRect();
        if (canvasInstance.current) {
            canvasInstance.current.dispose();
        }
        canvasInstance.current = new fabric.Canvas(canvasRef.current, {
            width,
            height,
            backgroundColor: '#fff',
            selection: false,
            defaultCursor: 'grab',
        })
    };


    const loadImage = (url) => {
        if (!canvasInstance.current || !canvasRef.current?.parentElement) return;

        fabric.Image.fromURL(url, (img) => {
            if (!canvasRef.current?.parentElement || !canvasInstance.current) return;
            const container = canvasRef.current.parentElement;
            const originalWidth = img.width;
            const originalHeight = img.height;
            setImgWidth(originalWidth)
            setImgHeight(originalHeight)
            // 计算基于容器短边的缩放比例
            const containerRatio = container.clientWidth / container.clientHeight;
            const imageRatio = originalWidth / originalHeight;
            let scale = 1;
            if (containerRatio > imageRatio) {
                scale = container.clientHeight / originalHeight;
            } else {
                scale = container.clientWidth / originalWidth;
            }
            // 第一次先获取缩放倍数，传导父组件比较后再往下渲染
            onZoom(scale, RESIZE_IMG_CANVASNAME.LEFT_CANVAS_NAME)
            if (!initMinZoom) return
            const leftPoint = (container.clientWidth - originalWidth * initMinZoom) / 2
            const topPoint = (container.clientHeight - originalHeight * initMinZoom) / 2

            canvasInstance.current.zoomToPoint({
                x: leftPoint,
                y: topPoint
            }, initMinZoom);

            img.set({
                left: leftPoint,
                top: topPoint,
                selectable: false,
                evented: false,
                hasControls: false,
                hasBorders: false,
                lockMovementX: false,
                lockMovementY: false,
                lockRotation: true,
                lockScalingX: true,
                lockScalingY: true
            });
            canvasInstance.current.clear();
            canvasInstance.current.add(img);
            canvasInstance.current.renderAll();
        });
    };

    // 处理窗口大小变化
    useEffect(() => {
        const resizeObserver = new ResizeObserver(entries => {
            if (!canvasInstance.current) return;

            const { width, height } = entries[0].contentRect;
            canvasInstance.current.setDimensions({ width, height });
            canvasInstance.current.requestRenderAll();
        });

        const parentDiv = canvasRef.current?.parentElement;
        if (parentDiv) {
            resizeObserver.observe(parentDiv);
        }
    }, []);


    useEffect(() => {
        initCanvas();
        loadImage(imageUrl);
    }, [imageUrl, initMinZoom]);

    // 移动画布
    useCanvasMovement(canvasInstance, ({ left, top }) => {
        onMove({ left, top })
    });


    // 处理缩放
    useCanvasZoom(
        canvasInstance,
        null,
        zoom, onZoom,
        initMinZoom,
        RESIZE_IMG_CANVASNAME.SCROLL_CANVAS
    )

    return (
        <div className={styles.canvasLeftCom}>
            <span className={styles.sizeTips}>原图：{imgWidth} * {imgHeight}px</span>
            <canvas ref={canvasRef} />
        </div>
    );
})

export default CanvasLeftCom;
